/* eslint-disable @typescript-eslint/no-shadow */
import _ from 'underscore';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import classNames from 'classnames';
import { Collapse, TextTitle } from '@bingads-webui-campaign/wizard-jumbo-card';

import { NewAdStrengthScore } from './new-ad-strength-score';
import { getStrengthIcon, newGenerateSuggestion } from './ad-strength-helper';
import { getNewAdStrengthStyles } from './styles';

const suggestionLimit = 5;

export function ResponsiveSearchAdStrength(props) {
  const {
    adItem,
    campaignId,
    adGroupId,
    onFetchAdStrength,
    logAdStrength,
    i18n,
  } = props;
  const [adStrength, setAdStrength] = useState();
  const [actionItems, setActionItems] = useState([]);
  const [priority, setPriority] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const iconName = getStrengthIcon(adStrength);
  const { Headlines, Descriptions } = adItem;
  const notEmptyHeadlines = useMemo(() => (Headlines ? Headlines.filter(item => item.Asset.Text !== '') : []), [Headlines]);
  const notEmptyDescriptions = useMemo(() => (Descriptions ? Descriptions.filter(item => item.Asset.Text !== '') : []), [Descriptions]);
  const IsCompleteAd = useMemo(() => (notEmptyHeadlines.length >= 3 && notEmptyDescriptions.length >= 2), [notEmptyHeadlines, notEmptyDescriptions]);
  const classes = useDefaultStyles(getNewAdStrengthStyles);
  const [activeKeys, setActiveKeys] = useState(['adStrength']);
  useEffect(() => {
    const rawActionItems = Object.keys(actionItems).map((key, index) => newGenerateSuggestion({
      i18n,
      actionItem: key,
      xValue: actionItems[key],
      priority: priority[index],
    }));

    const sortedSuggestions=_.sortBy(rawActionItems, suggestion => suggestion.priority || 100);

    setSuggestions(sortedSuggestions.slice(0, suggestionLimit));
  }, [Descriptions, Headlines, actionItems, adStrength, i18n, priority]);

  const lazyFetchNewAdStrength = useMemo(() => _.debounce((ad, campaignId, adGroupId, logAdStrength) => {
    onFetchAdStrength(ad, campaignId, adGroupId).then((response) => {
      const {
        Strength,
        ActionItemsV2,
        Priorities,
      } = response;

      setAdStrength(Strength);
      setActionItems(ActionItemsV2 ? JSON.parse(ActionItemsV2) : {});
      setPriority(Priorities || []);
      logAdStrength && logAdStrength({ Strength, ActionItemsV2, Priorities });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, 1000), []);

  useEffect(() => {
    if (IsCompleteAd && campaignId) {
      lazyFetchNewAdStrength({ Headlines: notEmptyHeadlines, Descriptions: notEmptyDescriptions, adId: adItem.Id }, campaignId, adGroupId || '0', logAdStrength);
    } else {
      setAdStrength('Incomplete');
    }
  }, [adGroupId, adItem.Id, campaignId, IsCompleteAd, lazyFetchNewAdStrength, logAdStrength, notEmptyDescriptions, notEmptyHeadlines]);

  return (
    <React.Fragment>
      <Collapse activeKeys={activeKeys} setActiveKeys={setActiveKeys}>
        <Collapse.Panel key="adStrength" title={<TextTitle titleKey={_TL_('Ad strength')} topicKey="pop_BA_RSA_AdStrength" as="h3" headingClassName={classes.cardTitle} />}>
          <div className={classNames('ad-strength-content', classes.container)}>
            { adStrength &&
              <div className="ad-strength-title">
                <FontIcon
                  iconName={iconName}
                  className={`icon ${adStrength}`}
                />
                {adStrength === 'Incomplete'? i18n.getString(_TL_('Incomplete')) : i18n.getString(_TL_(`${adStrength} Performance`))}
              </div>
            }
            <NewAdStrengthScore
              strength={adStrength}
            />
            { adStrength !== 'Excellent' && adStrength !== 'Incomplete' &&
              <div className="ad-strength-suggestions">
                <ul className="ad-strength-actions">
                  {_.map(suggestions, suggestion => (
                    <li>
                      <FontIcon
                        iconName="CRMCustomerInsightsApp"
                        className="suggestion-icon"
                      />
                      {suggestion.suggestion}
                    </li>
              ))}
                </ul>
              </div>
              }
            { adStrength === 'Incomplete' &&
            <div className="ad-strength-suggestions">
              <ul className="ad-strength-actions">
                { notEmptyHeadlines.length < 3 &&
                  <li>
                    <FontIcon
                      iconName="CRMCustomerInsightsApp"
                      className="suggestion-icon"
                    />
                    {i18n.getString(_TL_('Add 3 headlines to start ad strength.'))}
                  </li>
                }
                { notEmptyDescriptions.length < 2 &&
                  <li>
                    <FontIcon
                      iconName="CRMCustomerInsightsApp"
                      className="suggestion-icon"
                    />
                    {i18n.getString(_TL_('Add 2 descriptions to start ad strength.'))}
                  </li>
                }
              </ul>
            </div>
              }
          </div>
        </Collapse.Panel>
      </Collapse>
    </React.Fragment>
  );
}

ResponsiveSearchAdStrength.propTypes = {
  adItem: PropTypes.shape({
    Id: PropTypes.number,
    Headlines: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    Descriptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,

  onFetchAdStrength: PropTypes.func.isRequired,

  logAdStrength: PropTypes.func,

  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,

  campaignId: PropTypes.number,

  adGroupId: PropTypes.number,
};

ResponsiveSearchAdStrength.defaultProps = {
  campaignId: null,
  adGroupId: null,
  logAdStrength: _.noop,
};
